import React, { Component } from 'react'
import { RasaContext } from 'context'
import { HeaderComponent } from 'components/header/component'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { RasaDataGrid, PagingSource } from '../../generic/rasaDataGrid'
import { Loading } from 'components/loading'
import * as Flash from '../../components/flash'
import * as Columns from '../../components/grid/columns'
import { DateTimeCell } from 'components/gridcells/datetime'
import { AddBlastModal } from './modals/addBlastModal'
import * as GenericRedux from 'generic/genericRedux'
import { GridRowClickEvent } from '@progress/kendo-react-grid'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { DashboardMenuOption } from 'components/dashboard-menu/component'
import { BlastsAPI } from './blasts-api'
import { BlastActionsCell } from './blastActionsCell'
import './styles.scss'
import { SUPPORT_MAIL_TO } from '../../constants'

interface UpcomingBlastsState {
  communityId: string,
  isLoading: boolean,
  panelWidth: number,
  blasts: any[],
  isLoadingBlasts: boolean,
  accountGuid: string,
  identityCommunityGuid: string,
  newsletterList: { id: number, name: string } | null,
  hasBlastsAccess: boolean,
  campaignAccountCommunityIdentifier: string,
  showPreviewModal: boolean,
  previewHtml: string,
  isLoadingPreview: boolean,
  sendTestEmail: string,
  currentPreviewBlast: any,
  previewContactId: number,
  previewSubscriberId: number,
  isSendingTest: boolean,
}

type UpcomingBlastsProps = GenericRedux.AllComponentPropsWithModal<any>

class UpcomingBlastsComponent extends Component<UpcomingBlastsProps, UpcomingBlastsState> {
  public static contextType = RasaContext

  constructor(props: UpcomingBlastsProps) {
    super(props)
    this.state = {
      communityId: '',
      isLoading: true,
      panelWidth: 0,
      blasts: [],
      isLoadingBlasts: false,
      accountGuid: '',
      identityCommunityGuid: '',
      newsletterList: null,
      hasBlastsAccess: false,
      campaignAccountCommunityIdentifier: '',
      showPreviewModal: false,
      previewHtml: '',
      isLoadingPreview: false,
      sendTestEmail: '',
      currentPreviewBlast: null,
      previewContactId: 0,
      previewSubscriberId: 0,
      isSendingTest: false,
    }
  }

  public componentDidMount() {
    window.addEventListener('resize', this.handleResize)
    this.initialize()
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  private handleResize = () => {
    this.setState({
      panelWidth: window.innerWidth - Columns.LEFT_PANEL_DEFAULT_WIDTH,
    })
  }

  private initialize = () => {
    this.context.user.init().then(({ person, activeCommunity }) => {
      const accountGuid = (activeCommunity.data || {}).community_account_guid
      const identityCommunityGuid = activeCommunity.data.identity_community_guid
      const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []
      const hasBlastsAccess = avlFeatures.indexOf(BillingPlanDetailCode.BLASTS) > -1
      const userEmail = person && person.email ? person.email : ''

      this.setState({
        communityId: activeCommunity.communityId,
        isLoading: false,
        panelWidth: window.innerWidth - Columns.LEFT_PANEL_DEFAULT_WIDTH,
        accountGuid,
        identityCommunityGuid,
        hasBlastsAccess,        
        sendTestEmail: userEmail,
      }, () => {
        if (hasBlastsAccess) {
          // get blast account info and set it in state
          BlastsAPI.fetchAccountInfo(accountGuid)
            .then((accountData) => {
              this.setState({
                campaignAccountCommunityIdentifier: accountData.identifier || activeCommunity.communityId
              })
              this.fetchBlasts(activeCommunity.communityId, accountGuid, identityCommunityGuid)
            })
            .catch(() => {
              this.context.store.dispatch(Flash.showFlashError('Failed to load account info'))
              // Continue to fetch blasts even if account info fails
              this.fetchBlasts(activeCommunity.communityId, accountGuid, identityCommunityGuid)
            })
        }
      })
    })
    .catch((error) => {
      this.setState({
        isLoading: false,
      }, () => {
        this.context.store.dispatch(Flash.showFlashError(error))
      })
    })
  }

  private fetchBlasts = (communityId: string, accountGuid: string, identityCommunityGuid: string) => {
    this.setState({ isLoadingBlasts: true })

    BlastsAPI.fetchListsAndBlasts(accountGuid)
      .then(([lists, blasts]) => {
        // Filter lists by newsletter_guid and source_type
        const newsletterList = lists.find((list: any) =>
          list.options && list.options.newsletter_guid === identityCommunityGuid &&
          list.options.source_type === 'newsletter'
        )

        if (!newsletterList) {
          this.setState({
            blasts: [],
            isLoadingBlasts: false,
            newsletterList: null,
          })
          return
        }

        // Filter blasts by the newsletter list ID and future/null send dates
        const now = new Date()
        const filteredBlasts = blasts.filter((blast: any) =>
          blast.lists && blast.lists.some((list: any) => list.id === newsletterList.id) &&
          (!blast.start_date || new Date(blast.start_date) >= now)
        )

        this.setState({
          blasts: filteredBlasts || [],
          isLoadingBlasts: false,
          newsletterList: {
            id: newsletterList.id,
            name: newsletterList.name,
          },
        })
      })
      .catch((error) => {
        this.setState({
          blasts: [],
          isLoadingBlasts: false,
        }, () => {
          this.context.store.dispatch(Flash.showFlashError('Failed to load blasts'))
        })
      })
  }

  private SubjectCell = (props: any) => {
    const subjectObj = props.dataItem.subject
    const subjectContent = subjectObj && subjectObj.content ? subjectObj.content : ''

    return (
      <td>
        {subjectContent}
      </td>
    )
  }

  private TemplateLinkCell = (props: any) => {
    const template = props.dataItem.template
    if (!template) {
      return <td></td>
    }

    // Extract community identifier and template GUID
    const [templateCommunityIdentifier, templateGuid] = template.split('/')

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()

      // Pass communityIdentifier as separate param only if different from current newsletter
      let url = `${DashboardMenuOption.customDesign}?template=${templateGuid}&type=blast`
      if (templateCommunityIdentifier !== this.state.identityCommunityGuid) {
        url += `&communityIdentifier=${templateCommunityIdentifier}`
      }

      window.location.href = url
    }

    return (
      <td onClick={handleClick} className="template-link-cell">
        Edit Template
      </td>
    )
  }

  private BlastActionsCellComponent = (props: any) => {
    return <BlastActionsCell
      {...props}
      canPreview={true}
      canSchedule={true}
      canSendNow={true}
      onPreview={this.handlePreview}
      onSchedule={this.handleSchedule}
      onSendNow={this.handleSendNow}
    />
  }

  private handlePreview = (blast: any) => {
    if (!this.state.newsletterList) {
      this.context.store.dispatch(Flash.showFlashError('Newsletter list not found'))
      return
    }

    this.setState({
      showPreviewModal: true,
      isLoadingPreview: true,
      previewHtml: '',
      currentPreviewBlast: blast
    })

    // Step 1: Fetch contacts from the list
    BlastsAPI.fetchContacts(this.state.accountGuid, this.state.newsletterList.id)
      .then((contacts) => {
        if (!contacts || contacts.length === 0) {
          throw new Error('No contacts found in the list')
        }

        // Use the first contact
        const contactId = contacts[0].contact_id

        // Store contact ID for send test
        this.setState({ previewContactId: contactId })

        // Step 2: Preview the blast with the contact
        return BlastsAPI.previewBlast(
          this.state.accountGuid,
          blast.id,
          contactId,
          blast.sequence_id,
          blast.campaign_id,
          this.state.newsletterList.id
        )
      })
      .then((response) => {
        const html = response.html || response
        const subscriberId = response.context && response.context.subscriber && response.context.subscriber.id ? response.context.subscriber.id : 1

        this.setState({
          previewHtml: html,
          previewSubscriberId: subscriberId,
          isLoadingPreview: false
        })
      })
      .catch((error) => {
        this.setState({
          showPreviewModal: false,
          isLoadingPreview: false
        })
        this.context.store.dispatch(Flash.showFlashError('Failed to load preview'))
      })
  }

  private closePreviewModal = () => {
    this.setState({
      showPreviewModal: false,
      previewHtml: '',
      isLoadingPreview: false,
      currentPreviewBlast: null,
      previewContactId: 0,
    })
  }

  private handleSendTest = () => {
    const blast = this.state.currentPreviewBlast
    if (!blast || !blast.subject) {
      this.context.store.dispatch(Flash.showFlashError('Blast subject not found'))
      return
    }

    if (!this.state.sendTestEmail) {
      this.context.store.dispatch(Flash.showFlashError('Please enter an email address'))
      return
    }

    this.setState({ isSendingTest: true })

    const subject = blast.subject.content || ''
    const preview = blast.subject.preview || ''

    BlastsAPI.sendTestBlast(
      this.state.accountGuid,
      this.state.previewHtml,
      subject,
      preview,
      blast.id,
      this.state.previewContactId,
      this.state.previewSubscriberId,
      this.state.sendTestEmail
    )
      .then(() => {
        this.setState({ isSendingTest: false })
        this.context.store.dispatch(Flash.showFlashMessage('Test email sent successfully'))
      })
      .catch((error) => {
        this.setState({ isSendingTest: false })
        this.context.store.dispatch(Flash.showFlashError('Failed to send test email'))
      })
  }

  private handleSchedule = (blast: any) => {
    this.openScheduleBlastModal(blast)
  }

  private handleSendNow = (blast: any) => {
    const now = new Date()

    // Step 1: Update the start_date to current datetime
    BlastsAPI.scheduleBlast(this.state.accountGuid, blast.id, now)
      .then(() => {
        // Step 2: Send the blast
        return BlastsAPI.sendBlastNow(this.state.accountGuid, blast.id, blast.sequence_id)
      })
      .then(() => {
        this.fetchBlasts(this.state.communityId, this.state.accountGuid, this.state.identityCommunityGuid)
        this.context.store.dispatch(Flash.showFlashMessage('Blast sent successfully'))
      })
      .catch((error) => {
        this.context.store.dispatch(Flash.showFlashError('Failed to send blast'))
      })
  }

  private getColumns = (): Columns.SizableColumn[] => {
    return [
      {
        field: 'name',
        title: 'Name',
        width: 250,
        sortable: true,
      },
      {
        field: 'description',
        title: 'Description',
        width: 250,
        sortable: true,
      },
      {
        cell: this.SubjectCell,
        field: 'subject',
        title: 'Subject',
        width: 300,
        sortable: false,
      },
      {
        cell: DateTimeCell,
        field: 'start_date',
        title: 'Send Date',
        width: 200,
        sortable: true,
      },
      {
        cell: this.TemplateLinkCell,
        field: 'template',
        title: 'Template',
        minWidth: 150,
        sortable: false,
      },
      {
        cell: this.BlastActionsCellComponent,
        title: '',
        width: 50,
        sortable: false,
      },
    ]
  }

  private handleAddBlast = () => {
    this.openAddBlastModal()
  }

  private editBlast = (blast: any) => {
    this.openEditBlastModal(blast)
  }

  private openAddBlastModal = () => {
    this.props.openModal(AddBlastModal.key, {
      name: '',
      description: '',
      subject: '',
      preview: '',
      start_date: null,
      modalType: 'add',
    })
  }

  private openEditBlastModal = (blast: any) => {
    const subjectObj = blast.subject

    this.props.openModal(AddBlastModal.key, {
      id: blast.id,
      name: blast.name || '',
      description: blast.description || '',
      subject: (subjectObj && subjectObj.content) || '',
      preview: (subjectObj && subjectObj.preview) || '',
      start_date: blast.start_date ? new Date(blast.start_date) : null,
      modalType: 'edit',
    })
  }

  private openScheduleBlastModal = (blast: any) => {
    this.props.openModal(AddBlastModal.key, {
      id: blast.id,
      start_date: blast.start_date ? new Date(blast.start_date) : null,
      modalType: 'schedule',
    })
  }

  private handleSaveBlast = (data: any) => {
    const modalType = data.modalType
    const isEdit = modalType === 'edit' || modalType === 'schedule'

    if (isEdit) {
      if (modalType === 'schedule') {
        // Schedule the blast (only update send date)
        return BlastsAPI.scheduleBlast(this.state.accountGuid, data.id, data.start_date)
          .then(() => {
            this.fetchBlasts(this.state.communityId, this.state.accountGuid, this.state.identityCommunityGuid)
            this.context.store.dispatch(Flash.showFlashMessage('Blast scheduled successfully'))
            return Promise.resolve()
          })
          .catch((error) => {
            this.context.store.dispatch(Flash.showFlashError('Failed to schedule blast'))
            return Promise.reject(error)
          })
      } else {
        // Full update of the blast
        return BlastsAPI.updateBlast(this.state.accountGuid, data.id, {
          name: data.name,
          description: data.description,
          subject: data.subject,
          preview: data.preview,
        })
          .then(() => {
            this.fetchBlasts(this.state.communityId, this.state.accountGuid, this.state.identityCommunityGuid)
            this.context.store.dispatch(Flash.showFlashMessage('Blast updated successfully'))
            return Promise.resolve()
          })
          .catch((error) => {
            this.context.store.dispatch(Flash.showFlashError('Failed to update blast'))
            return Promise.reject(error)
          })
      }
    } else {
      // Create new blast
      if (!this.state.newsletterList) {
        this.context.store.dispatch(Flash.showFlashError('Newsletter list not found'))
        return Promise.reject(new Error('Newsletter list not found'))
      }

      let templateGuid: string

      // Step 1: Create the blast template
      return BlastsAPI.createBlastTemplate(
        this.state.accountGuid,
        data.name
      )
        .then((result) => {
          templateGuid = result.templateGuid
          // Step 2: Create the blast with the template
          return BlastsAPI.createBlast(
            this.state.accountGuid,
            this.state.campaignAccountCommunityIdentifier,
            data.name,
            data.description,
            this.state.newsletterList,
            templateGuid,
            data.subject,
            data.preview
          )
        })
        .then(() => {
          this.fetchBlasts(this.state.communityId, this.state.accountGuid, this.state.identityCommunityGuid)
          this.context.store.dispatch(Flash.showFlashMessage('Blast created successfully'))

          // Pass communityIdentifier as separate param only if different from current newsletter
          let url = `${DashboardMenuOption.customDesign}?template=${templateGuid}&type=blast`
          if (this.state.campaignAccountCommunityIdentifier !== this.state.identityCommunityGuid) {
            url += `&communityIdentifier=${this.state.campaignAccountCommunityIdentifier}`
          }

          window.location.href = url
          return Promise.resolve()
        })
        .catch((error) => {
          this.context.store.dispatch(Flash.showFlashError('Failed to create blast'))
          return Promise.reject(error)
        })
    }
  }

  private renderPreviewModal() {
    return (
      <Modal
        isOpen={this.state.showPreviewModal}
        size="xl"
        fade={false}
        centered={true}
        toggle={this.closePreviewModal}
        className="blast-preview-modal"
      >
        <ModalHeader toggle={this.closePreviewModal}>Blast Preview</ModalHeader>
        <ModalBody>
          {this.state.isLoadingPreview ? (
            <div className="preview-loading">
              <Loading size="64" />
            </div>
          ) : (
            <div className="blast-preview-container">
              <div className="preview-content">
                <div dangerouslySetInnerHTML={{ __html: this.state.previewHtml }} />
              </div>
              <div className="preview-actions">
                <div className="section">
                  <div className="top-block">
                    <span className="big-title">Send Test</span>
                  </div>
                  <div className="title send-to">
                    Send to:
                    <Input
                      type="text"
                      value={this.state.sendTestEmail}
                      onChange={(e) => this.setState({ sendTestEmail: e.target.value })}
                      placeholder="Enter email address"
                      className="send-test-input"
                    />
                  </div>
                  <div className="send-submit">
                    <Button
                      className="send-test-button"
                      onClick={this.handleSendTest}
                      disabled={this.state.isSendingTest || !this.state.sendTestEmail}
                    >
                      {this.state.isSendingTest ? 'Sending...' : 'Send'}
                    </Button>
                    <div className="send-email-warning">
                      <p>Send a test blast to view your design and content.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={this.closePreviewModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  public render() {
    if (this.state.isLoading) {
      return <Loading size="64" />
    }

    if (!this.state.hasBlastsAccess) {
      return (
        <div className="upcoming-blasts-container">
          <div className="header-container">
            <HeaderComponent
              title={'BLASTS'}
              subTitle={'Upcoming'}
              description={[`Blast campaigns are not available on your current plan. Please contact <a href=${SUPPORT_MAIL_TO}>support</a>.`]}
            />
          </div>
        </div>
      )
    }

    return (
      <div className="upcoming-blasts-container">
        {this.renderPreviewModal()}
        <div className="header-container">
          <AddBlastModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            saveModal={this.props.saveModal}
            updateModal={this.props.updateModal}
            onSave={this.handleSaveBlast}
          />
          <HeaderComponent
            title={'BLASTS'}
            subTitle={'Upcoming'}
            description={['View and manage your upcoming blast campaigns']}
          />
          <div className="add-new-blast-wrapper">
            <Button className="add-button add-new-blast" onClick={this.handleAddBlast}>
              <span className="button-text">Add Blast</span>
            </Button>
          </div>
        </div>
        {this.state.isLoadingBlasts ? (
          <div className="main-loading"><Loading size="64" /></div>
        ) : (
          <div className="blasts-grid-container">
            <RasaDataGrid
              entityName="blast"
              datasetName=""
              datasetParams={[]}
              editDefaultState={false}
              sortable={true}
              pageable={true}
              pageSize={20}
              pagingSource={PagingSource.local}
              data={this.state.blasts}
              onDataReceived={() => {}}
              onRowClick={(e: GridRowClickEvent) => this.editBlast(e.dataItem)}
            >
              {new Columns.SizableColumns(this.getColumns()).buildColumns(this.state.panelWidth)}
            </RasaDataGrid>
          </div>
        )}
      </div>
    )
  }
}

export default GenericRedux.registerNewComponentWithModals<any>(
  UpcomingBlastsComponent,
  'upcoming_blasts',
  [AddBlastModal.key],
)
